import { rngNext } from '@vpgc/core/rng';
import {
  BASE_GATHER_SECONDS,
  ACTIVE_TECH_DEFINITIONS,
  COMBO_GATHER_SPEED_PER_TIER,
  COMBO_MOVE_SPEED_PER_TIER,
  COMBO_WINDOW_SECONDS,
  FEATURE_PICKUP_STREAK,
  GOO_MOVEMENT_MULTIPLIER,
  PLAYER_BASE_SPEED,
  PLAYER_VISION_RADIUS,
  RESOURCE_IDS,
  TOOL_RECIPE_DEFINITIONS,
  canAfford,
  streakThreshold,
  techById,
  toolRecipeById,
  type ResourceId,
} from '../domain/content.js';
import {
  INPUT_ACTION,
  INPUT_CLOSE_PANEL,
  INPUT_DOWN,
  INPUT_LEFT,
  INPUT_OPEN_DEBUG_PANEL,
  INPUT_OPEN_BUILD_MENU,
  INPUT_OPEN_INVENTORY,
  INPUT_OPEN_TECH_TREE,
  INPUT_OPEN_TOOLS_PANEL,
  INPUT_RIGHT,
  INPUT_UP,
  type Direction,
  type GameState,
  type Input,
  type ItemCommand,
  type DebugCommand,
  type TechId,
  type ToolCommand,
  type TrashKind,
  type UiFocusScope,
} from '../contract.js';
import {
  buildingById,
  canPlacePendingBuilding,
  getBuildingAtTile,
  handleBuildCommand,
  handlePanelCommand,
  handlePanelItemMove,
  handleTransferItem,
  placePendingBuilding,
  updateBuildings,
} from './buildings.js';
import { cleanGooTile, isGooTile, updateGoo } from './goo.js';
import { addResourceItem, addTrashItem, addUsableItem, hasEmptySlot, hasTrashCapacity, hasUsableItem, removeResourceItem, removeUsableItem } from './inventory.js';
import { getTileAmount, isPermanentWall, pixelToTile, revealTile, setTileAmount, tileCenter } from './tiles.js';
import { maybeAwardZoneTech } from './zones.js';

const PLAYER_RADIUS = 9;
const GATHER_INTERACTION_RANGE = 1;
const DEBUG_RESOURCE_AMOUNT = 20;
const DEBUG_OPTION_COUNT = 6;
const FLAMETHROWER_BIOMASS_PER_SECOND = 1;
const FLAMETHROWER_DAMAGE_PER_SECOND = 2.4;

export function step(state: GameState, input: Input, dtSec: number): GameState {
  state.tick += 1;
  state.rng = rngNext(state.rng);
  state.fxEvents.length = 0;

  let pickedUpThisTick = false;

  if (input.selectedSlot !== undefined) {
    state.player.selectedSlot = clampSlot(state, input.selectedSlot);
    state.ui.inventoryFocus = state.player.selectedSlot;
  }
  if (input.inventoryMove !== undefined) moveInventoryItem(state, input.inventoryMove.fromSlot, input.inventoryMove.toSlot);
  if (input.transferItem !== undefined) handleTransferItem(state, input.transferItem.from, input.transferItem.slot);
  if (input.panelItemMove !== undefined) handlePanelItemMove(state, input.panelItemMove.from, input.panelItemMove.fromSlot, input.panelItemMove.to, input.panelItemMove.toSlot);
  if (input.uiFocus !== undefined) handleUiFocus(state, input.uiFocus.scope, input.uiFocus.index);

  if ((input.mask & INPUT_OPEN_BUILD_MENU) !== 0) handleBuildCommand(state, 'open');
  if ((input.mask & INPUT_OPEN_INVENTORY) !== 0) openInventory(state);
  if ((input.mask & INPUT_OPEN_TECH_TREE) !== 0) openTechTree(state);
  if ((input.mask & INPUT_OPEN_TOOLS_PANEL) !== 0) state.ui.toolsPanelOpen = false;
  if ((input.mask & INPUT_OPEN_DEBUG_PANEL) !== 0) openDebugPanel(state);
  if ((input.mask & INPUT_CLOSE_PANEL) !== 0) closeAllUi(state);

  if (input.panelCommand !== undefined) handlePanelCommand(state, input.panelCommand);
  if (input.buildCommand !== undefined) handleBuildCommand(state, input.buildCommand);
  if (input.techCommand !== undefined) handleTechCommand(state, input.techCommand);
  if (input.itemCommand !== undefined) handleItemCommand(state, input.itemCommand);
  if (input.toolCommand !== undefined) handleToolCommand(state, input.toolCommand);
  if (input.debugCommand !== undefined) handleDebugCommand(state, input.debugCommand);

  const panelOpen =
    state.ui.buildMenuOpen ||
    state.ui.inventoryOpen ||
    state.ui.techTreeOpen ||
    state.ui.toolsPanelOpen ||
    state.ui.debugPanelOpen ||
    buildingById(state, state.ui.openPanelBuildingId) !== undefined;

  if (!panelOpen) {
    movePlayer(state, input, dtSec);
    pickedUpThisTick = handleWorldAction(state, input, dtSec) || pickedUpThisTick;
  } else {
    state.player.gather = null;
    state.player.toolUseTimerSec = 0;
  }

  updateBuildings(state, dtSec, (kind, resource, fromX, fromY, toX, toY) => addFx(state, kind, resource, fromX, fromY, toX, toY));
  updateGoo(state, dtSec);
  updateStreak(state, pickedUpThisTick, dtSec);
  revealAroundPlayer(state);
  maybeAwardZoneTech(state);

  return state;
}

function movePlayer(state: GameState, input: Input, dtSec: number): boolean {
  let dx = 0;
  let dy = 0;
  if ((input.mask & INPUT_LEFT) !== 0) dx -= 1;
  if ((input.mask & INPUT_RIGHT) !== 0) dx += 1;
  if ((input.mask & INPUT_UP) !== 0) dy -= 1;
  if ((input.mask & INPUT_DOWN) !== 0) dy += 1;

  if (dx === 0 && dy === 0) return false;

  if (Math.abs(dx) > Math.abs(dy)) state.player.facing = dx > 0 ? 'east' : 'west';
  else state.player.facing = dy > 0 ? 'south' : 'north';

  const len = Math.hypot(dx, dy);
  const comboSpeed = 1 + state.streak.tier * state.tech.levels.movement_speed * COMBO_MOVE_SPEED_PER_TIER;
  const moveSpeed = state.player.speed * comboSpeed * movementMultiplier(state, state.player.x, state.player.y);
  const nextX = state.player.x + (dx / len) * moveSpeed * dtSec;
  const nextY = state.player.y + (dy / len) * moveSpeed * dtSec;

  const beforeX = state.player.x;
  const beforeY = state.player.y;
  if (!wouldCollide(state, nextX, state.player.y)) state.player.x = nextX;
  if (!wouldCollide(state, state.player.x, nextY)) state.player.y = nextY;
  return Math.hypot(state.player.x - beforeX, state.player.y - beforeY) > 0.01;
}

function movementMultiplier(state: GameState, x: number, y: number): number {
  return isGooTile(state, pixelToTile(x), pixelToTile(y)) ? GOO_MOVEMENT_MULTIPLIER : 1;
}

function wouldCollide(state: GameState, x: number, y: number): boolean {
  const left = pixelToTile(x - PLAYER_RADIUS);
  const right = pixelToTile(x + PLAYER_RADIUS);
  const top = pixelToTile(y - PLAYER_RADIUS);
  const bottom = pixelToTile(y + PLAYER_RADIUS);
  return (
    isPermanentWall(left, top) ||
    isPermanentWall(right, top) ||
    isPermanentWall(left, bottom) ||
    isPermanentWall(right, bottom) ||
    getTileAmount(state, left, top) > 0 ||
    getTileAmount(state, right, top) > 0 ||
    getTileAmount(state, left, bottom) > 0 ||
    getTileAmount(state, right, bottom) > 0
  );
}

function handleWorldAction(state: GameState, input: Input, dtSec: number): boolean {
  if (state.player.selectedTool !== 'hands') state.player.selectedTool = 'hands';
  if (selectedToolAvailable(state) === false) {
    state.player.selectedTool = 'hands';
    state.ui.statusMessage = 'Hands selected.';
  }

  state.player.toolUseTimerSec = 0;
  if ((input.mask & INPUT_ACTION) !== 0) performHandsAction(state, input);
  if (input.actionHeld === true) retargetHeldGather(state, input);
  return updateGather(state, input, dtSec);
}

function performHandsAction(state: GameState, input: Input): boolean {
  const playerTileX = pixelToTile(state.player.x);
  const playerTileY = pixelToTile(state.player.y);
  const target = chooseActionTarget(state, input, playerTileX, playerTileY);

  if (canPlacePendingBuilding(state, target.x, target.y, playerTileX, playerTileY)) {
    placePendingBuilding(state, target.x, target.y);
    return false;
  }

  const targetBuilding = getBuildingAtTile(state, target.x, target.y) ?? getBuildingAtTile(state, playerTileX, playerTileY);
  if (targetBuilding !== undefined) {
    state.ui.openPanelBuildingId = targetBuilding.id;
    state.ui.buildMenuOpen = false;
    state.ui.inventoryOpen = false;
    state.ui.techTreeOpen = false;
    state.ui.toolsPanelOpen = false;
    state.ui.debugPanelOpen = false;
    return false;
  }

  if (canGatherTile(state, target.x, target.y)) {
    startGather(state, target.x, target.y);
  }
  return false;
}

function openInventory(state: GameState): void {
  state.ui.inventoryOpen = true;
  state.ui.buildMenuOpen = false;
  state.ui.techTreeOpen = false;
  state.ui.toolsPanelOpen = false;
  state.ui.debugPanelOpen = false;
  state.ui.openPanelBuildingId = null;
  state.ui.inventoryFocus = clampSlot(state, state.ui.inventoryFocus);
  state.player.selectedSlot = state.ui.inventoryFocus;
}

function openTechTree(state: GameState): void {
  state.ui.techTreeOpen = true;
  state.ui.buildMenuOpen = false;
  state.ui.inventoryOpen = false;
  state.ui.toolsPanelOpen = false;
  state.ui.debugPanelOpen = false;
  state.ui.openPanelBuildingId = null;
  state.ui.techTreeFocus = wrap(state.ui.techTreeFocus, ACTIVE_TECH_DEFINITIONS.length);
}

function openDebugPanel(state: GameState): void {
  state.ui.debugPanelOpen = true;
  state.ui.buildMenuOpen = false;
  state.ui.inventoryOpen = false;
  state.ui.techTreeOpen = false;
  state.ui.toolsPanelOpen = false;
  state.ui.openPanelBuildingId = null;
  state.ui.pendingBuildType = null;
  state.ui.debugPanelFocus = wrap(state.ui.debugPanelFocus, DEBUG_OPTION_COUNT);
}

function closeAllUi(state: GameState): void {
  state.ui.openPanelBuildingId = null;
  state.ui.buildMenuOpen = false;
  state.ui.pendingBuildType = null;
  state.ui.inventoryOpen = false;
  state.ui.techTreeOpen = false;
  state.ui.toolsPanelOpen = false;
  state.ui.debugPanelOpen = false;
}

function moveInventoryItem(state: GameState, fromSlot: number, toSlot: number): void {
  const from = clampSlot(state, fromSlot);
  const to = clampSlot(state, toSlot);
  if (from === to) {
    state.player.selectedSlot = to;
    return;
  }
  const moving = state.inventory.slots[from];
  const target = state.inventory.slots[to];
  state.inventory.slots[to] = moving;
  state.inventory.slots[from] = target;
  state.player.selectedSlot = to;
  state.ui.inventoryFocus = to;
}

function handleUiFocus(state: GameState, scope: UiFocusScope, index: number): void {
  if (scope === 'inventory' && state.ui.inventoryOpen) {
    state.ui.inventoryFocus = clampSlot(state, index);
    state.player.selectedSlot = state.ui.inventoryFocus;
    return;
  }
  if (scope === 'buildMenu' && state.ui.buildMenuOpen) {
    state.ui.buildMenuFocus = wrap(index, 4);
    return;
  }
  if (scope === 'buildingPanel' && buildingById(state, state.ui.openPanelBuildingId) !== undefined) {
    state.ui.buildingPanelFocus = wrap(index, 5);
    return;
  }
  if (scope === 'techTree' && state.ui.techTreeOpen) {
    state.ui.techTreeFocus = wrap(index, ACTIVE_TECH_DEFINITIONS.length);
    return;
  }
  if (scope === 'toolsPanel' && state.ui.toolsPanelOpen) {
    state.ui.toolsPanelFocus = wrap(index, TOOL_RECIPE_DEFINITIONS.length);
    return;
  }
  if (scope === 'debugPanel' && state.ui.debugPanelOpen) {
    state.ui.debugPanelFocus = wrap(index, DEBUG_OPTION_COUNT);
  }
}

function handleTechCommand(state: GameState, tech: TechId): void {
  if (!state.ui.techTreeOpen || state.tech.points <= 0) return;
  if (!ACTIVE_TECH_DEFINITIONS.some((definition) => definition.id === tech)) return;
  const def = techById(tech);
  if (state.tech.levels[tech] >= def.maxLevel) return;
  state.tech.points -= 1;
  state.tech.levels[tech] += 1;
  if (tech === 'movement_speed') state.player.speed = PLAYER_BASE_SPEED;
  state.ui.statusMessage = `${def.name} upgraded.`;
}

function harvestTile(state: GameState, x: number, y: number): 'trash' | 'goo' | null {
  const amount = getTileAmount(state, x, y);
  if (amount <= 0) {
    if (!cleanGooTile(state, x, y)) return null;
    addFx(state, 'burn', 'biomass', tileCenter(x), tileCenter(y), tileCenter(x), tileCenter(y));
    return 'goo';
  }

  const kind = trashKindForTile(state, x, y, amount);
  if (!addTrashItem(state, kind)) return null;
  setTileAmount(state, x, y, amount - 1);
  state.stats.garbageHarvested += 1;
  if (amount - 1 <= 0) state.stats.tilesCleared += 1;
  addFx(state, 'pickup', kind, tileCenter(x), tileCenter(y), state.player.x, state.player.y);
  return 'trash';
}

function startGather(state: GameState, x: number, y: number): void {
  if (!canGatherTile(state, x, y)) return;
  const existing = state.player.gather;
  if (existing !== null && existing.targetTileX === x && existing.targetTileY === y) return;
  state.player.gather = {
    targetTileX: x,
    targetTileY: y,
    elapsedSec: 0,
    durationSec: gatherDuration(state),
  };
}

function retargetHeldGather(state: GameState, input: Input): void {
  const playerTileX = pixelToTile(state.player.x);
  const playerTileY = pixelToTile(state.player.y);
  const target = chooseActionTarget(state, input, playerTileX, playerTileY);
  if (isGatherTargetInRange(state, target.x, target.y) && canGatherTile(state, target.x, target.y)) {
    startGather(state, target.x, target.y);
    return;
  }
  const gather = state.player.gather;
  if (gather !== null && (!isGatherTargetInRange(state, gather.targetTileX, gather.targetTileY) || !canGatherTile(state, gather.targetTileX, gather.targetTileY))) {
    state.player.gather = null;
  }
}

function canGatherTile(state: GameState, x: number, y: number): boolean {
  const amount = getTileAmount(state, x, y);
  if (amount > 0) return hasTrashCapacity(state);
  return isGooTile(state, x, y);
}

function updateGather(state: GameState, input: Input, dtSec: number): boolean {
  const gather = state.player.gather;
  if (gather === null) return false;
  if (input.actionHeld !== true || !isGatherTargetInRange(state, gather.targetTileX, gather.targetTileY) || !canGatherTile(state, gather.targetTileX, gather.targetTileY)) {
    state.player.gather = null;
    return false;
  }
  gather.durationSec = gatherDuration(state);
  gather.elapsedSec += dtSec;
  let picked = false;
  while (gather.elapsedSec >= gather.durationSec) {
    gather.elapsedSec -= gather.durationSec;
    const harvested = harvestTile(state, gather.targetTileX, gather.targetTileY);
    if (harvested === null) {
      state.player.gather = null;
      return picked;
    }
    if (harvested === 'trash') picked = true;
    if (!canGatherTile(state, gather.targetTileX, gather.targetTileY)) {
      state.player.gather = null;
      return picked;
    }
  }
  return picked;
}

function gatherDuration(state: GameState): number {
  const comboBonus = state.streak.tier * state.tech.levels.gather_speed * COMBO_GATHER_SPEED_PER_TIER;
  return BASE_GATHER_SECONDS / (1 + comboBonus);
}

function handleItemCommand(state: GameState, command: ItemCommand): void {
  if (command === 'cycleTool') cycleTool(state, 1);
  else if (command === 'cycleToolBackward') cycleTool(state, -1);
  else if (command === 'selectGrenade') selectTool(state, 'grenade');
  else if (command === 'selectFlamethrower') selectTool(state, 'flamethrower');
  else selectTool(state, 'hands');
}

function handleToolCommand(state: GameState, command: ToolCommand): void {
  if (!state.ui.toolsPanelOpen) return;
  if (command === 'grenade') craftGrenade(state);
  else if (command === 'flamethrower') craftFlamethrower(state);
}

function handleDebugCommand(state: GameState, command: DebugCommand): void {
  if (!state.ui.debugPanelOpen) return;
  if (command === 'grantTechPoint') {
    grantTestTechPoint(state);
    return;
  }
  if (command === 'grantGrenade') {
    state.tech.levels.grenade = Math.max(1, state.tech.levels.grenade);
    state.ui.statusMessage = addUsableItem(state, 'grenade') ? 'Debug: added a grenade.' : 'Debug: need an empty slot for a grenade.';
    return;
  }
  if (command === 'grantFlamethrower') {
    state.tech.levels.flamethrower = Math.max(1, state.tech.levels.flamethrower);
    state.ui.statusMessage = addUsableItem(state, 'flamethrower') ? 'Debug: added a flamethrower.' : 'Debug: need an empty slot for a flamethrower.';
    return;
  }
  if (command === 'grantAllResources') {
    let added = 0;
    for (const resource of RESOURCE_IDS) {
      if (addResourceItem(state, resource, DEBUG_RESOURCE_AMOUNT)) added += 1;
    }
    state.ui.statusMessage = added === RESOURCE_IDS.length ? 'Debug: added all resources.' : 'Debug: bag full before adding every resource.';
    return;
  }
  const resource = command === 'grantScrap'
    ? 'scrap'
    : command === 'grantPlastic'
      ? 'plastic'
      : command === 'grantGlass'
        ? 'glass'
        : 'biomass';
  state.ui.statusMessage = addResourceItem(state, resource, DEBUG_RESOURCE_AMOUNT)
    ? `Debug: added ${DEBUG_RESOURCE_AMOUNT} ${resource}.`
    : `Debug: need inventory space for ${resource}.`;
}

function craftGrenade(state: GameState): void {
  const recipe = toolRecipeById('grenade');
  if (state.tech.levels[recipe.requiredTech] <= 0) {
    state.ui.statusMessage = 'Unlock Grenade in the tech tree first.';
    return;
  }
  if (!canAfford(state.resources, recipe.cost) || !hasEmptySlot(state)) {
    state.ui.statusMessage = 'Need scrap, plastic, and an empty slot for a grenade.';
    return;
  }
  spendToolRecipeCost(state, recipe.cost);
  addUsableItem(state, 'grenade');
  state.ui.statusMessage = 'Grenade crafted.';
}

function craftFlamethrower(state: GameState): void {
  const recipe = toolRecipeById('flamethrower');
  if (state.tech.levels[recipe.requiredTech] <= 0) {
    state.ui.statusMessage = 'Unlock Flamethrower in the tech tree first.';
    return;
  }
  if (hasUsableItem(state, 'flamethrower')) {
    state.ui.statusMessage = 'You already have a flamethrower.';
    return;
  }
  if (!canAfford(state.resources, recipe.cost) || !hasEmptySlot(state)) {
    state.ui.statusMessage = 'Need scrap, plastic, glass, and an empty slot for a flamethrower.';
    return;
  }
  spendToolRecipeCost(state, recipe.cost);
  addUsableItem(state, 'flamethrower');
  state.ui.statusMessage = 'Flamethrower crafted.';
}

function spendToolRecipeCost(state: GameState, cost: GameState['resources']): void {
  for (const resource of RESOURCE_IDS) {
    if (cost[resource] > 0) removeResourceItem(state, resource, cost[resource]);
  }
}

function useGrenade(state: GameState, input: Input): void {
  if (state.tech.levels.grenade <= 0 || !hasUsableItem(state, 'grenade')) return;
  const playerTileX = pixelToTile(state.player.x);
  const playerTileY = pixelToTile(state.player.y);
  const target = chooseActionTarget(state, input, playerTileX, playerTileY);
  if (!removeUsableItem(state, 'grenade')) return;
  let destroyed = 0;
  for (let y = target.y - 4; y <= target.y + 4; y++) {
    for (let x = target.x - 4; x <= target.x + 4; x++) {
      const distance = Math.hypot(x - target.x, y - target.y);
      if (distance > 4) continue;
      const damage = grenadeDamage(distance);
      destroyed += damageTileTrash(state, x, y, damage, 'blast');
    }
  }
  state.ui.statusMessage = `Grenade cleared ${destroyed} trash.`;
  if (!hasUsableItem(state, 'grenade')) state.player.selectedTool = 'hands';
}

function useFlamethrower(state: GameState, input: Input, dtSec: number): void {
  if (state.tech.levels.flamethrower <= 0 || !hasUsableItem(state, 'flamethrower')) return;
  if (state.resources.biomass <= 0) {
    state.player.toolUseTimerSec = 0;
    state.ui.statusMessage = 'Need biomass for the flamethrower.';
    return;
  }
  const originX = pixelToTile(state.player.x);
  const originY = pixelToTile(state.player.y);
  const facing = aimedDirection(state, input);
  const forward = directionVector(facing);
  const burnSeconds = Math.max(0, dtSec);
  state.player.toolUseTimerSec += burnSeconds * FLAMETHROWER_BIOMASS_PER_SECOND;
  while (state.player.toolUseTimerSec >= 1) {
    if (!removeResourceItem(state, 'biomass', 1)) {
      state.player.toolUseTimerSec = 0;
      state.ui.statusMessage = 'Need biomass for the flamethrower.';
      return;
    }
    state.player.toolUseTimerSec -= 1;
  }

  let destroyed = 0;
  for (let forwardStep = 1; forwardStep <= 3; forwardStep++) {
    for (let spread = -forwardStep; spread <= forwardStep; spread++) {
      const side = facing === 'north' || facing === 'south'
        ? { x: spread, y: 0 }
        : { x: 0, y: spread };
      const falloff = forwardStep === 1 ? 1 : forwardStep === 2 ? 0.62 : 0.35;
      destroyed += damageTileTrash(state, originX + forward.x * forwardStep + side.x, originY + forward.y * forwardStep + side.y, FLAMETHROWER_DAMAGE_PER_SECOND * burnSeconds * falloff, 'burn');
    }
  }
  state.ui.statusMessage = `Flamethrower burned ${destroyed} trash.`;
}

function damageTileTrash(state: GameState, x: number, y: number, damage: number, kind: 'burn' | 'blast'): number {
  if (isPermanentWall(x, y)) return 0;
  const amount = getTileAmount(state, x, y);
  if (amount <= 0) return 0;
  const removed = Math.min(amount, Math.max(1, Math.floor(damage)));
  const resource = trashKindForTile(state, x, y, amount);
  setTileAmount(state, x, y, amount - removed);
  if (amount - removed <= 0) state.stats.tilesCleared += 1;
  state.stats.trashDestroyed += removed;
  addFx(state, kind, resource, tileCenter(x), tileCenter(y), tileCenter(x), tileCenter(y));
  return removed;
}

function updateStreak(state: GameState, pickedUpThisTick: boolean, dtSec: number): void {
  if (!state.features.pickupStreak || !FEATURE_PICKUP_STREAK) return;
  if (pickedUpThisTick) {
    state.streak.value += 1;
    state.streak.timeRemainingSec = COMBO_WINDOW_SECONDS;
    while (state.streak.value >= streakThreshold(state.streak.tier + 1)) state.streak.tier += 1;
    return;
  }
  if (state.streak.value <= 0) return;
  state.streak.timeRemainingSec = Math.max(0, state.streak.timeRemainingSec - dtSec);
  if (state.streak.timeRemainingSec > 0) return;
  state.streak.value = 0;
  state.streak.tier = 0;
}

function revealAroundPlayer(state: GameState): void {
  const originX = pixelToTile(state.player.x);
  const originY = pixelToTile(state.player.y);
  for (let y = originY - PLAYER_VISION_RADIUS; y <= originY + PLAYER_VISION_RADIUS; y++) {
    for (let x = originX - PLAYER_VISION_RADIUS; x <= originX + PLAYER_VISION_RADIUS; x++) {
      if (Math.hypot(x - originX, y - originY) > PLAYER_VISION_RADIUS) continue;
      if (hasLineOfSight(state, originX, originY, x, y)) revealTile(state, x, y);
    }
  }
}

function hasLineOfSight(state: GameState, fromX: number, fromY: number, toX: number, toY: number): boolean {
  const dx = toX - fromX;
  const dy = toY - fromY;
  const dist = Math.hypot(dx, dy);
  if (dist <= 1.5) return true;
  const steps = Math.max(1, Math.ceil(dist * 2));
  let budget = 8;
  for (let i = 1; i <= steps; i++) {
    const tx = Math.round(fromX + (dx * i) / steps);
    const ty = Math.round(fromY + (dy * i) / steps);
    if (isPermanentWall(tx, ty)) return false;
    budget -= 1 + getTileAmount(state, tx, ty) * 0.55;
    if (budget < 0) return false;
  }
  return true;
}

function isGatherTargetInRange(state: GameState, targetX: number, targetY: number): boolean {
  const playerTileX = pixelToTile(state.player.x);
  const playerTileY = pixelToTile(state.player.y);
  return Math.abs(targetX - playerTileX) + Math.abs(targetY - playerTileY) <= GATHER_INTERACTION_RANGE;
}

function selectedToolAvailable(state: GameState): boolean {
  if (state.player.selectedTool === 'hands') return true;
  if (state.player.selectedTool === 'grenade') return state.tech.levels.grenade > 0 && hasUsableItem(state, 'grenade');
  return state.tech.levels.flamethrower > 0 && hasUsableItem(state, 'flamethrower');
}

function selectTool(state: GameState, tool: 'hands' | 'grenade' | 'flamethrower'): void {
  if (tool !== 'hands' && !hasUsableItem(state, tool)) {
    state.player.selectedTool = 'hands';
    state.ui.statusMessage = `${tool === 'grenade' ? 'Grenade' : 'Flamethrower'} unavailable. Hands selected.`;
    return;
  }
  state.player.selectedTool = tool;
  state.player.gather = null;
  state.player.toolUseTimerSec = 0;
  state.ui.statusMessage = tool === 'hands' ? 'Hands selected.' : `${tool === 'grenade' ? 'Grenade' : 'Flamethrower'} selected.`;
}

function cycleTool(state: GameState, direction: 1 | -1): void {
  const tools: Array<'hands' | 'grenade' | 'flamethrower'> = ['hands'];
  if (state.tech.levels.grenade > 0 && hasUsableItem(state, 'grenade')) tools.push('grenade');
  if (state.tech.levels.flamethrower > 0 && hasUsableItem(state, 'flamethrower')) tools.push('flamethrower');
  const current = tools.indexOf(state.player.selectedTool);
  selectTool(state, tools[((current < 0 ? 0 : current) + direction + tools.length) % tools.length] ?? 'hands');
}

function grantTestTechPoint(state: GameState): void {
  state.tech.points += 1;
  state.ui.statusMessage = 'Test tech point granted.';
}

function grenadeDamage(distance: number): number {
  if (distance <= 1) return 6;
  if (distance <= 2) return 3;
  if (distance <= 3) return 2;
  return 1;
}

function aimedDirection(state: GameState, input: Input): Direction {
  if (input.targetTileX === undefined || input.targetTileY === undefined) return state.player.facing;
  const playerTileX = pixelToTile(state.player.x);
  const playerTileY = pixelToTile(state.player.y);
  const dx = input.targetTileX - playerTileX;
  const dy = input.targetTileY - playerTileY;
  if (Math.abs(dx) > Math.abs(dy)) return dx >= 0 ? 'east' : 'west';
  if (Math.abs(dy) > 0) return dy >= 0 ? 'south' : 'north';
  return state.player.facing;
}

function chooseActionTarget(state: GameState, input: Input, playerTileX: number, playerTileY: number): { x: number; y: number } {
  if (input.targetTileX !== undefined && input.targetTileY !== undefined) {
    return { x: input.targetTileX, y: input.targetTileY };
  }
  const delta = directionVector(state.player.facing);
  return { x: playerTileX + delta.x, y: playerTileY + delta.y };
}

function directionVector(direction: Direction): { x: -1 | 0 | 1; y: -1 | 0 | 1 } {
  switch (direction) {
    case 'north':
      return { x: 0, y: -1 };
    case 'east':
      return { x: 1, y: 0 };
    case 'south':
      return { x: 0, y: 1 };
    case 'west':
      return { x: -1, y: 0 };
  }
}

function trashKindForTile(state: GameState, x: number, y: number, amount: number): TrashKind {
  const mix = (state.seed ^ Math.imul(x + 101, 73856093) ^ Math.imul(y - 37, 19349663) ^ Math.imul(amount + 11, 83492791)) >>> 0;
  return RESOURCE_IDS[mix % RESOURCE_IDS.length];
}

function addFx(state: GameState, kind: 'pickup' | 'dropoff' | 'burn' | 'blast', resource: ResourceId, fromX: number, fromY: number, toX: number, toY: number): void {
  state.fxEvents.push({
    id: state.tick * 1000 + state.fxEvents.length,
    kind,
    resource,
    fromX,
    fromY,
    toX,
    toY,
    amount: 1,
  });
}

function clampSlot(state: GameState, slot: number): number {
  return Math.max(0, Math.min(state.inventory.slots.length - 1, Math.floor(slot)));
}

function wrap(value: number, count: number): number {
  if (count <= 0) return 0;
  return ((value % count) + count) % count;
}
